import React from 'react';
import { Alert } from 'components';
import s from './styles.module.scss';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null 
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { 
      hasError: true, 
      error,
      errorId: Date.now().toString(36) + Math.random().toString(36).substr(2)
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console for debugging
    console.error('🚨 Error Boundary Caught Error:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      errorId: this.state.errorId,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent
    });

    // Update state with error info
    this.setState({
      errorInfo
    });

    // Show user-friendly error message
    Alert.error('Something went wrong. Please try refreshing the page.');
  }

  handleRetry = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null 
    });
  };

  handleGoHome = () => {
    window.location.href = '/home';
  };

  handleRefresh = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className={s.errorBoundary}>
          <div className={s.errorContainer}>
            <div className={s.errorIcon}>
              <span className="material-icons">error_outline</span>
            </div>
            
            <h1 className={s.errorTitle}>Oops! Something went wrong</h1>
            
            <p className={s.errorMessage}>
              We're sorry, but something unexpected happened. Our team has been notified.
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className={s.errorDetails}>
                <summary>Error Details (Development)</summary>
                <div className={s.errorStack}>
                  <strong>Error:</strong> {this.state.error.toString()}
                  <br />
                  <strong>Error ID:</strong> {this.state.errorId}
                  <br />
                  <strong>Component Stack:</strong>
                  <pre>{this.state.errorInfo?.componentStack}</pre>
                </div>
              </details>
            )}

            <div className={s.errorActions}>
              <button 
                className={s.retryButton}
                onClick={this.handleRetry}
              >
                <span className="material-icons">refresh</span>
                Try Again
              </button>
              
              <button 
                className={s.homeButton}
                onClick={this.handleGoHome}
              >
                <span className="material-icons">home</span>
                Go Home
              </button>
              
              <button 
                className={s.refreshButton}
                onClick={this.handleRefresh}
              >
                <span className="material-icons">refresh</span>
                Refresh Page
              </button>
            </div>

            <div className={s.errorFooter}>
              <p>If this problem persists, please contact support.</p>
              <p>Error ID: {this.state.errorId}</p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 